import type { Json } from '@/integrations/supabase/types';
import { supabase } from '@/integrations/supabase/client';
import { createFitnessId } from '@/lib/fitnessId';
import { personalRoutineDay, replaceRoutineDay, type RoutineProgression } from './routines';
import type { FreeWorkoutItem } from './freeWorkoutBuilder';

export interface PersonalRoutineSaveInput {
  ownerId: string; name: string; items: readonly FreeWorkoutItem[]; planId?: string; dayIndex?: number; progression?: RoutineProgression; newPlanId?: string;
}
const stableJson = (value: unknown): string => {
  if (Array.isArray(value)) return '[' + value.map(stableJson).join(',') + ']';
  if (value && typeof value === 'object') return '{' + Object.keys(value).sort().map(key => JSON.stringify(key) + ':' + stableJson((value as Record<string, unknown>)[key])).join(',') + '}';
  return JSON.stringify(value) ?? 'null';
};
// Both callers validate their routine domain objects before this JSON boundary.
// Serialization materializes optional interface members as the SDK's Json type.
const routineDaysJson = (days: readonly unknown[]): Json => JSON.parse(JSON.stringify(days));
const assertCurrent = (current: () => boolean) => { if (!current()) throw new Error('Fitness account changed'); };
/** Called only by an explicit Save routine / Update routine gesture; RLS remains authoritative. */
export async function savePersonalRoutine(input: PersonalRoutineSaveInput, current: () => boolean): Promise<string> {
  assertCurrent(current);
  const auth = await supabase.auth.getUser();
  assertCurrent(current);
  if (auth.error || auth.data.user?.id !== input.ownerId) throw auth.error ?? new Error('Fitness account changed');
  if (input.planId) {
    const existing = await supabase.from('workout_plans').select('id,user_id,days').eq('id', input.planId).eq('user_id', input.ownerId).single();
    assertCurrent(current);
    if (existing.error || existing.data?.user_id !== input.ownerId) throw existing.error ?? new Error('Routine unavailable');
    const days = replaceRoutineDay(existing.data.days, input.dayIndex ?? 0, input.items, input.progression);
    const saved = await supabase.from('workout_plans').update({ days: routineDaysJson(days) })
      .eq('id', input.planId).eq('user_id', input.ownerId).eq('days', JSON.stringify(existing.data.days)).select('id');
    assertCurrent(current);
    if (saved.error || saved.data?.length !== 1 || saved.data[0].id !== input.planId) throw saved.error ?? new Error('Routine changed; reload before saving');
    return input.planId;
  }
  const id = input.newPlanId ?? createFitnessId(), day = personalRoutineDay(input.name, input.items, input.progression);
  const saved = await supabase.from('workout_plans').upsert({
    id, user_id: input.ownerId, name: input.name.trim(), days: routineDaysJson([day]), is_active: false, source: 'manual',
  }, { onConflict: 'id', ignoreDuplicates: true }).select('id,user_id');
  assertCurrent(current);
  if (saved.error || saved.data?.length !== 1) {
    // A retry uses the same id. Verify the original insert instead of creating a duplicate.
    const readback = await supabase.from('workout_plans').select('id,user_id,name,days').eq('id', id).eq('user_id', input.ownerId).maybeSingle();
    assertCurrent(current);
    if (readback.error || readback.data?.id !== id || readback.data?.user_id !== input.ownerId || readback.data?.name !== input.name.trim() || stableJson(readback.data?.days) !== stableJson([day])) throw saved.error ?? readback.error ?? new Error('Routine save not confirmed');
  } else if (saved.data[0].id !== id || saved.data[0].user_id !== input.ownerId) throw new Error('Routine save not confirmed');
  return id;
}
